import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import FileTypeIcon from "@/components/icons/FileTypeIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { tableStandardProps } from "@/utils/componentUtils";
import { formatFileSize, formatLocaleDateTime } from "@/utils/formatUtils";
import { Dropdown, Table } from "antd";
import { ItemType } from "antd/es/menu/interface";
import { ColumnsType } from "antd/lib/table";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import { TableSizes } from "../../../../common/constants";
import { DOCUMENT_NODE_ROUTE_PATHS } from "../../paths";
import { DocumentNodeTree } from "../../types";
import { findNodeByTreePath } from "../../utils";
import styles from "./DocumentNodeListView.module.scss";

interface Props {
  hasPermission: boolean;
  currentPath: string;
  fullTree: DocumentNodeTree[];
  selectedNodes: DocumentNodeTree[];
  onNodesSelect: (nodes: DocumentNodeTree[]) => void;
  onNodeOpen: (tree?: DocumentNodeTree) => void;
  onFileDownload: (node: DocumentNodeTree) => void;
  onRenameNodeClick: (node: DocumentNodeTree) => void;
  onMoveNodesClick: (nodes: DocumentNodeTree[]) => void;
  onDeleteNodesClick: (nodes: DocumentNodeTree[]) => void;
}

const ACTION = {
  DOWNLOAD: "download",
  RENAME: "rename",
  MOVE: "move",
  DELETE: "delete",
};

export const DocumentNodeListView = ({
  hasPermission,
  fullTree,
  currentPath,
  selectedNodes,
  onNodesSelect,
  onNodeOpen,
  onFileDownload,
  onRenameNodeClick,
  onMoveNodesClick,
  onDeleteNodesClick,
}: Props) => {
  const { t } = useTranslation();

  const currentNodes: DocumentNodeTree[] = currentPath
    ? (findNodeByTreePath(fullTree, currentPath)?.children ?? [])
    : fullTree;

  const dataSource =
    currentPath !== ""
      ? ([
          {
            id: "",
            optimisticLockVersion: 0,
            createdAt: "",
            updatedAt: "",
            name: "...",
            treePath: "",
            type: undefined,
            file: undefined,
            children: [],
          },
          ...currentNodes,
        ] as DocumentNodeTree[])
      : currentNodes;

  const handleOpenClick = (record: DocumentNodeTree) => {
    if (!record.id) {
      onNodeOpen();
      return;
    }

    onNodeOpen(record);
  };

  const handleActionClick = (actionKey: string, record: DocumentNodeTree) => {
    switch (actionKey) {
      case ACTION.DOWNLOAD:
        onFileDownload(record);
        break;
      case ACTION.RENAME:
        onRenameNodeClick(record);
        break;
      case ACTION.MOVE:
        onMoveNodesClick([record]);
        break;
      case ACTION.DELETE:
        onDeleteNodesClick([record]);
        break;
    }
  };

  const renderRecordName = (record: DocumentNodeTree) => {
    const content = (
      <>
        {record.id === "" ? (
          <AntIcon type="left-square" />
        ) : record.file ? (
          <FileTypeIcon contentType={record.file.contentType} />
        ) : (
          <AntIcon type="open-folder" />
        )}
        <span className="margin-left-tiny">{record.name}</span>
      </>
    );

    return record.file ? (
      <Link
        to={generatePath(DOCUMENT_NODE_ROUTE_PATHS.attachment.to, { id: record.id })}
        target="_blank"
        className="no-link-color"
      >
        {content}
      </Link>
    ) : (
      <span className={classNames(styles.documentsItemLink)} onClick={() => handleOpenClick(record)}>
        {content}
      </span>
    );
  };

  const columns: ColumnsType<DocumentNodeTree> = [
    {
      key: "name",
      title: t("common.filename"),
      ellipsis: { showTitle: false },
      width: 435,
      render: (_, record) => <Ellipsis tooltip={record.name}>{renderRecordName(record)}</Ellipsis>,
    },
    {
      key: "size",
      title: t("common.filesize"),
      width: 150,
      render: (_, record) => (record.file ? formatFileSize(record.file.size) : null),
    },
    {
      key: "updatedAt",
      title: t("common.updatedAt"),
      width: 150,
      render: (_, record) => formatLocaleDateTime(record.updatedAt),
    },
    {
      key: "actions",
      width: 100,
      align: "right",
      render: (_, record) => {
        if (record.id !== "") {
          const items: ItemType[] = [];

          if (record.file) {
            items.push({
              key: ACTION.DOWNLOAD,
              label: <ActionTextIcon icon="download" color="green" text={t("documentNode.menu.download")} />,
            });
          }

          if (hasPermission) {
            items.push(
              ...[
                {
                  key: ACTION.RENAME,
                  label: <ActionTextIcon icon="edit" color="blue" text={t("documentNode.menu.rename")} />,
                },
                {
                  key: ACTION.MOVE,
                  label: <ActionTextIcon icon="retweet" color="orange" text={t("documentNode.menu.move")} />,
                },
                {
                  key: ACTION.DELETE,
                  label: <ActionTextIcon icon="delete" color="red" text={t("documentNode.menu.delete")} />,
                },
              ],
            );
          }

          return (
            items.length > 0 && (
              <Dropdown
                menu={{ items, onClick: (event) => handleActionClick(event.key, record) }}
                trigger={["click"]}
                onOpenChange={() => onNodesSelect([])}
              >
                <span>
                  <ActionTextIcon icon="menu-unfold" color="blue" text={t("common.options")} />
                </span>
              </Dropdown>
            )
          );
        }

        return null;
      },
    },
  ];

  return (
    <Table<DocumentNodeTree>
      {...tableStandardProps()}
      dataSource={dataSource}
      columns={columns}
      style={{ maxWidth: 960, width: "100%" }}
      scroll={{ y: currentNodes.length > 14 ? TableSizes.SMALL : undefined }}
      rowSelection={
        hasPermission
          ? {
              onChange: (_, selectedRows) => {
                onNodesSelect(selectedRows);
              },
              selectedRowKeys: selectedNodes.map((row) => row.id),
              getCheckboxProps: (record) => ({
                disabled: record.id === "",
              }),
            }
          : undefined
      }
      expandable={{
        expandRowByClick: false,
        expandIcon: () => undefined,
      }}
      pagination={false}
    />
  );
};
